import { plateFont } from '../../utils/fonts';
import * as React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import PlaceIcon from '@mui/icons-material/Place';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventIcon from '@mui/icons-material/Event';
import { useTranslation } from 'react-i18next';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

export function TooltipRow({ icon: Icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0.5 }}>
      <Icon sx={{ fontSize: 15, color: 'text.secondary', mt: '2px', flexShrink: 0 }} />
      <Typography sx={{ fontSize: '12px', color: 'text.primary', lineHeight: 1.4 }}>{children}</Typography>
    </Box>
  );
}

function InternalNotesSection({ notes }) {
  const { t } = useTranslation();
  if (!notes?.length) return null;
  return (
    <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <StickyNote2Icon sx={{ fontSize: 13, color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>{t('Internal notes')}</Typography>
      </Box>
      {notes.map((note, idx) => (
        <Typography key={idx} sx={{ fontSize: '11px', color: 'text.primary', lineHeight: 1.4, mb: 0.25, pl: 0.5 }}>
          {note.text}
          {note.created_by && (
            <Typography component="span" sx={{ fontSize: '10px', color: 'text.secondary', ml: 0.5 }}>— {note.created_by}</Typography>
          )}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Tooltip content for maintenance / work order events.
 */
export function MaintenanceTooltipContent({ event, config, timeString, employeeNames, serviceBayName, otherSchedules, tagName, tagColor }) {
  const { t } = useTranslation();

  const formatScheduleTime = (scheduleEvent) => {
    if (!scheduleEvent.start_time || !scheduleEvent.end_time) return '';
    const start = new Date(scheduleEvent.start_time);
    const end = new Date(scheduleEvent.end_time);
    const dateStr = `${start.getDate()}.${start.getMonth() + 1}.`;
    const startStr = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
    const endStr = `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}`;
    return `${dateStr} ${startStr} - ${endStr}`;
  };

  return (
    <Box sx={{ p: 1.5, minWidth: 180 }}>
      <Box sx={{ mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ ...plateFont, fontWeight: 700, fontSize: '13px' }}>
            {event.vehicle_registration}
          </Typography>
          {event.work_number && (
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>#{event.work_number}</Typography>
          )}
        </Box>
        {(event.vehicle_manufacturer || event.vehicle_model) && (
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mt: 0.25 }}>
            {[event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' ')}
          </Typography>
        )}
      </Box>
      <Chip
        label={t(config.labelKey)}
        size="small"
        sx={{ mb: 1, height: 20, fontSize: '11px', bgcolor: config.bg, color: config.text, borderLeft: `3px solid ${config.color}` }}
      />
      {/* The card may be too short to show the tag name, so the tooltip always carries it. */}
      {tagName && (
        <Chip
          data-testid="event-tooltip-color-tag"
          label={tagName}
          size="small"
          icon={
            <Box
              component="span"
              sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: tagColor, ml: '8px !important' }}
            />
          }
          variant="outlined"
          sx={{ mb: 1, ml: 0.5, height: 20, fontSize: '11px', borderColor: tagColor }}
        />
      )}
      {event.customer_name && <TooltipRow icon={PersonIcon}>{event.customer_name}</TooltipRow>}
      {event.description && <TooltipRow icon={DescriptionIcon}>{event.description}</TooltipRow>}
      <TooltipRow icon={AccessTimeIcon}>{timeString}</TooltipRow>
      {employeeNames && <TooltipRow icon={GroupIcon}>{employeeNames}</TooltipRow>}
      {serviceBayName && <TooltipRow icon={PlaceIcon}>{serviceBayName}</TooltipRow>}
      {event.has_active_booking && <TooltipRow icon={EventAvailableIcon}>{t('Online booking')}</TooltipRow>}
      {event.parts_order_status && PARTS_ORDER_CONFIG[event.parts_order_status] && (
        <Box sx={{ mt: 0.5 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: 500,
              backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].bg,
              color: PARTS_ORDER_CONFIG[event.parts_order_status].color,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].color }} />
            {t(`parts_order.${event.parts_order_status}_short`)}
          </Box>
        </Box>
      )}
      {otherSchedules?.length > 0 && (
        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <EventIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.secondary' }}>
              {t('Other scheduled times')}
            </Typography>
          </Box>
          {otherSchedules.map((s) => (
            <Typography key={s.id} sx={{ fontSize: '11px', color: 'text.primary', lineHeight: 1.4, mb: 0.25, pl: 0.5 }}>
              {formatScheduleTime(s)}
            </Typography>
          ))}
        </Box>
      )}
      <InternalNotesSection notes={event.internal_notes} />
    </Box>
  );
}

/**
 * Tooltip content for unavailability events.
 */
export function UnavailabilityTooltipContent({ typeLabel, config, employeeName, timespan, title }) {
  return (
    <Box sx={{ p: 1.5, minWidth: 160 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <BeachAccessIcon sx={{ fontSize: 16, color: config.color }} />
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: config.color }}>{typeLabel}</Typography>
      </Box>
      {employeeName && <TooltipRow icon={PersonIcon}>{employeeName}</TooltipRow>}
      <TooltipRow icon={AccessTimeIcon}>{timespan}</TooltipRow>
      {title && (
        <Typography sx={{ fontSize: '12px', color: 'text.primary', mt: 0.5 }}>{title}</Typography>
      )}
    </Box>
  );
}

/**
 * Tooltip content for calendar note events.
 */
export function CalendarNoteTooltipContent({ config, timespan, createdByName, content }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 1.5, minWidth: 160 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <StickyNote2Icon sx={{ fontSize: 16, color: config.color }} />
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: config.text }}>{t('Note')}</Typography>
      </Box>
      <TooltipRow icon={AccessTimeIcon}>{timespan}</TooltipRow>
      {createdByName && <TooltipRow icon={PersonIcon}>{createdByName}</TooltipRow>}
      {content && (
        <Typography sx={{ fontSize: '12px', color: 'text.primary', mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {content}
        </Typography>
      )}
    </Box>
  );
}
